import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { cn } from "@/lib/utils"

interface Option<V extends string | number> {
  id: V
  label: string
}

export interface MultiSelectOptionGroup<V extends string | number> {
  label: string
  options: Option<V>[]
  /** Render group labels and options in a muted tone (e.g. deleted accounts). */
  muted?: boolean
}

interface MenuPos {
  top: number
  left: number
  width: number
}

interface MultiSelectDropdownProps<V extends string | number> {
  label: string
  options?: Option<V>[]
  optionGroups?: MultiSelectOptionGroup<V>[]
  selectedIds: V[]
  onChange: (ids: V[]) => void
  minWidth?: string
  dropdownMinWidth?: string
  searchable?: boolean
  searchPlaceholder?: string
  showApplyClearActions?: boolean
  /**
   * When true, the trigger always shows `label` (never first-option + count).
   * Use for pickers like Columns where the selection summary is confusing.
   */
  alwaysShowLabel?: boolean
  /** Merged onto the outer wrapper (e.g. `w-full min-w-0` for mobile filter rows). */
  className?: string
}

/**
 * A click-to-open dropdown that lets the user pick multiple options via
 * checkboxes. Replaces `<select multiple>`, which renders as a
 * permanently-open listbox and visually breaks dense filter bars.
 *
 * The menu is portaled to `document.body` with `position: fixed` so it
 * stays visible when a parent uses `overflow-x-auto` (Dashboard / Ads
 * Manager filter rows).
 *
 * Closes on click-outside and Escape. By default the trigger shows the
 * selected option label (or first label + remaining count) instead of
 * the static fallback `label` when something is picked. Pass
 * `alwaysShowLabel` to keep the trigger text fixed.
 */
export function MultiSelectDropdown<V extends string | number>({
  label,
  options = [],
  optionGroups,
  selectedIds,
  onChange,
  minWidth = "160px",
  dropdownMinWidth,
  searchable = false,
  searchPlaceholder = "Search...",
  showApplyClearActions = false,
  alwaysShowLabel = false,
  className,
}: MultiSelectDropdownProps<V>) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [draftSelectedIds, setDraftSelectedIds] = useState<V[]>(selectedIds)
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Closing resets the menu position and search query. Adjusted during
  // render (both are only written while open) instead of in effects.
  if (!open && menuPos !== null) setMenuPos(null)
  if (!open && search !== "") setSearch("")

  // While open with Apply/Clear, keep the draft in sync with the
  // committed selection (on open, and if the parent changes it).
  const [draftSyncedFrom, setDraftSyncedFrom] = useState({
    open,
    selectedIds,
    showApplyClearActions,
  })
  if (
    draftSyncedFrom.open !== open ||
    draftSyncedFrom.selectedIds !== selectedIds ||
    draftSyncedFrom.showApplyClearActions !== showApplyClearActions
  ) {
    setDraftSyncedFrom({ open, selectedIds, showApplyClearActions })
    if (open && showApplyClearActions) setDraftSelectedIds(selectedIds)
  }

  useLayoutEffect(() => {
    if (!open) return
    const el = triggerRef.current
    if (!el) return

    const update = () => {
      const r = el.getBoundingClientRect()
      setMenuPos({
        top: r.bottom + 4,
        left: r.left,
        width: r.width,
      })
    }
    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapperRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  const selectedCount = selectedIds.length
  const effectiveSelectedIds = showApplyClearActions ? draftSelectedIds : selectedIds

  const flatOptions = optionGroups
    ? optionGroups.flatMap((group) => group.options)
    : options

  const selectedLabels = selectedIds
    .map((id) => flatOptions.find((o) => o.id === id)?.label)
    .filter((value): value is string => Boolean(value))

  let triggerLabel = label
  if (!alwaysShowLabel) {
    if (
      selectedLabels.length > 0 &&
      selectedLabels.length === flatOptions.length &&
      flatOptions.length > 1
    ) {
      triggerLabel = label
    } else if (selectedLabels.length === 1) {
      triggerLabel = selectedLabels[0]
    } else if (selectedLabels.length > 1) {
      triggerLabel = `${selectedLabels[0]} (+${selectedLabels.length - 1})`
    } else if (selectedCount > 0) {
      triggerLabel = `${label} (${selectedCount})`
    }
  }

  const triggerTitle = alwaysShowLabel
    ? selectedLabels.length > 0
      ? selectedLabels.join(", ")
      : label
    : selectedLabels.length > 1
      ? selectedLabels.join(", ")
      : triggerLabel

  const filteredFlatOptions = !searchable
    ? flatOptions
    : flatOptions.filter((o) =>
        o.label.toLowerCase().includes(search.trim().toLowerCase()),
      )

  const filteredGroups = optionGroups
    ? optionGroups
        .map((group) => ({
          ...group,
          options: !searchable
            ? group.options
            : group.options.filter((o) =>
                o.label.toLowerCase().includes(search.trim().toLowerCase()),
              ),
        }))
        .filter((group) => group.options.length > 0)
    : null

  const toggle = (id: V) => {
    if (showApplyClearActions) {
      if (draftSelectedIds.includes(id)) {
        setDraftSelectedIds(draftSelectedIds.filter((x) => x !== id))
      } else {
        setDraftSelectedIds([...draftSelectedIds, id])
      }
    } else {
      if (selectedIds.includes(id)) {
        onChange(selectedIds.filter((x) => x !== id))
      } else {
        onChange([...selectedIds, id])
      }
    }
  }

  const menuBody =
    open &&
    menuPos && (
    <div
      ref={menuRef}
      className="flex max-h-[320px] flex-col rounded border border-[#4a4a4a] bg-[#2d2d2d] py-1 shadow-lg"
      style={{
        position: "fixed",
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        minWidth: dropdownMinWidth ?? minWidth,
        zIndex: 200,
      }}
    >
      {searchable ? (
        <div className="shrink-0 px-2 pb-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-7 w-full rounded border border-[#555] bg-[#252525] px-2 text-xs text-white placeholder:text-[#888] focus:border-[#8b9aff] focus:outline-none"
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {filteredGroups ? (
          filteredGroups.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#888]">No options</div>
          ) : (
            filteredGroups.map((group, groupIndex) => (
              <div key={group.label}>
                {groupIndex > 0 ? (
                  <div className="mx-2 my-1 border-t border-[#444]" />
                ) : null}
                <div
                  className={cn(
                    "px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider",
                    group.muted ? "text-[#777]" : "text-[#999]",
                  )}
                >
                  {group.label}
                </div>
                {group.options.map((o) => (
                  <label
                    key={String(o.id)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-[#3a3a3a]",
                      group.muted ? "text-[#999]" : "text-[#e0e0e0]",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={effectiveSelectedIds.includes(o.id)}
                      onChange={() => toggle(o.id)}
                      className="accent-[#8b9aff]"
                    />
                    <span className="whitespace-normal break-words" title={o.label}>
                      {o.label}
                    </span>
                  </label>
                ))}
              </div>
            ))
          )
        ) : filteredFlatOptions.length === 0 ? (
          <div className="px-3 py-2 text-xs text-[#888]">No options</div>
        ) : (
          filteredFlatOptions.map((o) => (
            <label
              key={String(o.id)}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-[#e0e0e0] hover:bg-[#3a3a3a]"
            >
              <input
                type="checkbox"
                checked={effectiveSelectedIds.includes(o.id)}
                onChange={() => toggle(o.id)}
                className="accent-[#8b9aff]"
              />
              <span className="whitespace-normal break-words" title={o.label}>
                {o.label}
              </span>
            </label>
          ))
        )}
      </div>

      {showApplyClearActions ? (
        <div className="flex shrink-0 items-center justify-center gap-2 border-t border-[#3a3a3a] bg-[#232323] px-3 py-2">
          <button
            type="button"
            className="flex-1 rounded bg-[#3d3d3d] px-3 py-1.5 text-xs font-medium text-[#ccc] transition-colors hover:bg-[#4a4a4a] hover:text-white"
            onClick={() => setDraftSelectedIds([])}
          >
            Clear all
          </button>
          <button
            type="button"
            className="flex-1 rounded bg-[#6366f1] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#5558e3]"
            onClick={() => {
              onChange(draftSelectedIds)
              setOpen(false)
            }}
          >
            Apply
          </button>
        </div>
      ) : null}
    </div>
    )

  return (
    <div
      ref={wrapperRef}
      className={cn("relative", className)}
      style={minWidth ? { minWidth } : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-[#555] bg-[#2d2d2d] px-3 text-left text-sm text-[#e0e0e0] shadow-sm transition-colors hover:bg-[#3a3a3a]"
      >
        <span className="min-w-0 truncate" title={triggerTitle}>
          {triggerLabel}
        </span>
        <span aria-hidden="true" className="text-[10px] text-[#888]">{"\u25BC"}</span>
      </button>
      {menuBody && typeof document !== "undefined"
        ? createPortal(menuBody, document.body)
        : null}
    </div>
  )
}
